import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LiquidImage from './LiquidImage';
import './ProjectsShowcase.css';

gsap.registerPlugin(ScrollTrigger);

const ProjectsShowcase = () => {
    const [currentProject, setCurrentProject] = useState(0);
    const [showTransition, setShowTransition] = useState(false);
    const containerRef = useRef(null);
    const isScrolling = useRef(false);

    // Projects data with full information
    const projects = [
        {
            id: 1,
            name: 'DRAWNO',
            category: 'DRAWING APP (WEBSITE)',
            description: 'Socket.io and WebRTC-powered collaborative whiteboard featuring real-time drawing synchronization, P2P voice chat, multi-page boards, and YouTube integration using React and Node.js',
            duration: '3 months',
            tech: ['React', 'Node.js', 'Socket.io'],
            image: '/images/Drawno.png',
            liveUrl: 'https://drawno-frontend.vercel.app',
            githubUrl: 'https://github.com/Pragyanshu08/drawno',
            year: '2025'
        },
        {
            id: 2,
            name: 'AURORA',
            category: 'SOCIAL MEDIA PLATFORM',
            description: 'Full-Stack Social Media Platform with Real-Time Chat & Video Communication',
            duration: '4 months',
            tech: ['React', 'Node.js', 'MongoDB', 'Socket.io'],
            image: '/images/Aurora.png',
            liveUrl: 'https://aurora-frontend-one.vercel.app/',
            githubUrl: 'https://github.com/Pragyanshu08/aurora',
            year: '2025'
        },
        {
            id: 3,
            name: 'VITS NOTES HUB',
            category: 'ACADEMIC & SOCIAL PLATFORM',
            description: 'A full-stack academic resource hub and student social network. Provides centralized access to question papers, syllabi, and study notes, with an integrated social system for students to share educational progress.',
            duration: '4 months',
            tech: ['MongoDB', 'Node.js', 'Express', 'EJS'],
            image: '/images/VitsNotesHub.png',
            liveUrl: 'https://vitsnoteshub.onrender.com/',
            githubUrl: 'https://github.com/Pragyanshu08/vitsnoteshub',
            year: '2024'
        },
    ];

    const totalProjects = projects.length;

    // Unified function to switch projects with liquid transition
    const switchToProject = (newIndex) => {
        if (isScrolling.current || newIndex < 0 || newIndex >= totalProjects) return;

        isScrolling.current = true;
        setShowTransition(true);

        // Switch project at peak of animation (when overlay is fully visible)
        setTimeout(() => {
            setCurrentProject(newIndex);
        }, 400);

        // Hide overlay after animation completes
        setTimeout(() => {
            setShowTransition(false);
            isScrolling.current = false;
        }, 1000);
    };

    // Character-by-character reveal animation for project title
    useEffect(() => {
        const titleElement = containerRef.current?.querySelector('.project-title');
        if (!titleElement) return;

        // Split title into characters
        const text = titleElement.textContent;
        titleElement.innerHTML = text.split('').map((char, index) =>
            `<span class="project-title-char" style="transition-delay: ${index * 0.03}s">${char === ' ' ? '&nbsp;' : char}</span>`
        ).join('');

        // Trigger animation after a brief delay
        const animationTimeout = setTimeout(() => {
            const chars = titleElement.querySelectorAll('.project-title-char');
            chars.forEach((char) => {
                char.style.opacity = '1';
                char.style.transform = 'translateY(0)';
                // Using exact easing from aneshk.design: cubic-bezier(.72, .14, .27, .87)
                char.style.transition = 'opacity 0.8s cubic-bezier(.72, .14, .27, .87), transform 0.8s cubic-bezier(.72, .14, .27, .87)';
            });
        }, 100);

        return () => {
            clearTimeout(animationTimeout);
        };
    }, [currentProject]);

    // Scroll locking mechanism for project switching
    useEffect(() => {
        const handleWheel = (e) => {
            const container = containerRef.current;
            if (!container) return;

            // Don't trigger if already in transition
            if (isScrolling.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            const rect = container.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            // Check if section is in view
            const isInView = rect.top <= viewportHeight * 0.2 && rect.bottom >= viewportHeight * 0.8;
            if (!isInView) return;

            const scrollingDown = e.deltaY > 0;
            const scrollingUp = e.deltaY < 0;

            // Add a threshold (e.g., 50) to make it less sensitive to accidental scrolls
            const scrollForce = Math.abs(e.deltaY);
            const threshold = 50;

            // Lock scrolling when in the section and navigating between projects
            if (scrollingDown && currentProject < totalProjects - 1) {
                e.preventDefault();
                e.stopPropagation();

                if (scrollForce > threshold) {
                    switchToProject(currentProject + 1);
                }
                return;
            }

            if (scrollingUp && currentProject > 0) {
                e.preventDefault();
                e.stopPropagation();

                if (scrollForce > threshold) {
                    switchToProject(currentProject - 1);
                }
                return;
            }

            // At boundaries, allow natural scrolling but prevent jitter
            if ((scrollingDown && currentProject === totalProjects - 1) ||
                (scrollingUp && currentProject === 0)) {
                // If we're at the very top or bottom, we don't prevent default
                // to allow the user to scroll out of the section.
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false, capture: true });

        return () => {
            window.removeEventListener('wheel', handleWheel, { capture: true });
        };
    }, [currentProject, totalProjects]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            const container = containerRef.current;
            if (!container) return;

            const rect = container.getBoundingClientRect();
            const isInView = rect.top <= window.innerHeight * 0.8 && rect.bottom >= 0;

            if (!isInView) return;

            switch (e.key) {
                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault();
                    if (currentProject > 0) {
                        switchToProject(currentProject - 1);
                    }
                    break;
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault();
                    if (currentProject < totalProjects - 1) {
                        switchToProject(currentProject + 1);
                    }
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentProject, totalProjects]);

    const project = projects[currentProject];

    return (
        <div className="projects-showcase" ref={containerRef}>
            <div className="project-content">
                {/* Two Column Layout */}
                <div className="project-layout">
                    {/* Left Column - Text Content */}
                    <div className="project-text">
                        {/* Project Number */}
                        <div className="project-number">
                            {String(currentProject + 1).padStart(2, '0')}/{String(totalProjects).padStart(2, '0')}
                        </div>

                        {/* Huge Project Title with character reveal */}
                        <div className="project-title-container">
                            <h1 className="project-title">{project.name}</h1>
                        </div>

                        {/* Category */}
                        <div className="project-category">{project.category} • {project.year}</div>

                        {/* Description */}
                        <p className="project-description">{project.description}</p>

                        {/* Tech Stack */}
                        <div className="project-tech">
                            <span className="tech-label">TECH STACK:</span>
                            <span className="tech-value">{project.tech.join(' • ')}</span>
                        </div>

                        {/* Project Links */}
                        <div className="project-links">
                            <a
                                href={project.liveUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-link live"
                            >
                                <span>VIEW LIVE</span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3m-2 16H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7Z" />
                                </svg>
                            </a>
                            <a
                                href={project.githubUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-link github"
                            >
                                <span>VIEW CODE</span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Right Column - Image with Liquid Effect */}
                    <div className="project-visual">
                        {showTransition ? (
                            <LiquidImage
                                imageSrc={project.image}
                                className="project-image-liquid"
                                autoAnimate={true}
                            />
                        ) : (
                            <img
                                src={project.image}
                                alt={project.name}
                                className="project-image"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Navigation Arrows */}
            <div className="nav-arrows">
                <button
                    className="nav-arrow up"
                    onClick={() => switchToProject(currentProject - 1)}
                    disabled={currentProject === 0 || isScrolling.current}
                >
                    ↑
                </button>
                <button
                    className="nav-arrow down"
                    onClick={() => switchToProject(currentProject + 1)}
                    disabled={currentProject === totalProjects - 1 || isScrolling.current}
                >
                    ↓
                </button>
            </div>
        </div>
    );
};

export default ProjectsShowcase;
